import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { Badge, Button } from "@/design-system/components/ui";
import { DataTable, type Column } from "@/components/layout/DataTable";
import { useAuth } from "@/hooks/useAuth";
import { useDealsList } from "@/hooks/deals/useDeals";
import { NewDealWizard } from "@/components/deals/NewDealWizard";
import {
  ageBadgeStatus,
  ageSeverity,
  dealLabel,
  formatCurrency,
  formatStageAge,
} from "@/components/deals/dealFormat";
import {
  ACTIVE_STAGES,
  DEAL_PERMISSIONS,
  STAGE_LABEL,
  subStateLabelFor,
  type DealListItemDto,
  type DealListQuery,
  type DealPipelineStage,
} from "@/types/deals";
import type { BadgeProps } from "@/design-system/components/ui";
import { useDateFormatter } from "@/utils/datetime";

type FilterMode = "active" | "terminated" | "archived";

const PAGE_SIZE = 20;

const FILTER_CHIPS: { key: FilterMode; label: string }[] = [
  { key: "active", label: "Active deals only" },
  { key: "terminated", label: "Include declined / withdrawn" },
  { key: "archived", label: "Include archived" },
];

const STAGE_BADGE_STATUS: Record<DealPipelineStage, NonNullable<BadgeProps["status"]>> = {
  Intake: "info",
  PreApproval: "gold",
  Underwriting: "gold",
  Closing: "finalized",
  Closed: "draft",
  Declined: "error",
  Withdrawn: "archived",
};

export interface BrokerDealsTabProps {
  brokerId: string;
}

export function BrokerDealsTab({ brokerId }: BrokerDealsTabProps) {
  const { hasPermission } = useAuth();
  const canCreate = hasPermission(DEAL_PERMISSIONS.Create);
  const { date } = useDateFormatter();

  const [filterMode, setFilterMode] = useState<FilterMode>("active");
  const [page, setPage] = useState(1);
  const [wizardOpen, setWizardOpen] = useState(false);

  const query: DealListQuery = useMemo(() => {
    const base: DealListQuery = { brokerId, page, pageSize: PAGE_SIZE };
    switch (filterMode) {
      case "active":
        return { ...base, stage: [...ACTIVE_STAGES], includeArchived: false };
      case "terminated":
        return { ...base, stage: ["Declined", "Withdrawn"], includeArchived: false };
      case "archived":
        return { ...base, includeArchived: true };
    }
  }, [brokerId, filterMode, page]);

  const dealsQuery = useDealsList(query);
  const rows = dealsQuery.data?.data ?? [];
  const totalCount = dealsQuery.data?.totalCount ?? 0;
  const pageCount = totalCount > 0 ? Math.ceil(totalCount / PAGE_SIZE) : 1;

  const columns: Column<DealListItemDto>[] = [
    {
      key: "dealNumber",
      header: "Deal #",
      render: (d) => (
        <Link
          to={`/pipeline/${d.id}`}
          className="font-mono text-caption text-gold-shadow hover:text-gold-highlight"
        >
          {d.dealNumber}
        </Link>
      ),
    },
    {
      key: "businessName",
      header: "Business",
      render: (d) => (
        <Link to={`/pipeline/${d.id}`} className="font-semibold hover:text-gold-shadow">
          {dealLabel(d)}
        </Link>
      ),
    },
    {
      key: "stage",
      header: "Stage",
      render: (d) => (
        <div className="flex flex-col gap-space-1">
          <Badge status={STAGE_BADGE_STATUS[d.stage]}>{STAGE_LABEL[d.stage]}</Badge>
          <span className="text-caption text-gray-mid">{subStateLabelFor(d.stage, d.subState)}</span>
        </div>
      ),
    },
    {
      key: "purchasePrice",
      header: "Price",
      className: "text-right",
      render: (d) => (
        <span className="font-mono tabular-nums">{formatCurrency(d.purchasePrice)}</span>
      ),
    },
    {
      key: "age",
      header: "Days in stage",
      render: (d) => {
        const sev = ageSeverity(d);
        return <Badge status={ageBadgeStatus(sev)}>{formatStageAge(d.stageEnteredDate)}</Badge>;
      },
    },
    {
      key: "createdAt",
      header: "Created",
      render: (d) => (
        <span className="text-caption text-gray-mid">
          {date(d.createdAt)}
        </span>
      ),
    },
  ];

  const showEmptyCta = !dealsQuery.isLoading && rows.length === 0 && filterMode === "active";

  return (
    <div className="flex flex-col gap-space-3">
      <div className="flex items-center justify-between gap-space-3 flex-wrap">
        <FilterChips
          value={filterMode}
          onChange={(next) => {
            setFilterMode(next);
            setPage(1);
          }}
        />
        {canCreate && (
          <Button size="sm" onClick={() => setWizardOpen(true)}>
            <Plus size={14} aria-hidden /> New deal
          </Button>
        )}
      </div>

      <DataTable
        columns={columns}
        rows={rows}
        loading={dealsQuery.isLoading}
        error={dealsQuery.isError ? "Could not load deals for this broker." : null}
        rowKey={(d) => d.id}
        emptyMessage={
          showEmptyCta
            ? "No deals for this broker yet."
            : filterMode === "terminated"
              ? "No declined or withdrawn deals."
              : filterMode === "archived"
                ? "No archived deals."
                : "No deals match the current filter."
        }
      />

      {showEmptyCta && canCreate && (
        <div className="text-center">
          <Button onClick={() => setWizardOpen(true)}>
            <Plus size={14} aria-hidden /> New deal
          </Button>
        </div>
      )}

      {totalCount > PAGE_SIZE && (
        <div className="flex items-center justify-between mt-space-2">
          <span className="text-label text-gray-mid">
            Page {page} of {pageCount} · {totalCount} total
          </span>
          <div className="flex gap-space-2">
            <Button
              variant="secondary"
              size="sm"
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </Button>
            <Button
              variant="secondary"
              size="sm"
              type="button"
              disabled={page >= pageCount}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Launched from this broker's context — prefill + skip the Broker step
          (still changeable via "Change broker" inside the wizard). */}
      <NewDealWizard
        open={wizardOpen}
        onClose={() => setWizardOpen(false)}
        initialBrokerId={brokerId}
      />
    </div>
  );
}

function FilterChips({
  value,
  onChange,
}: {
  value: FilterMode;
  onChange: (next: FilterMode) => void;
}) {
  return (
    <div role="radiogroup" aria-label="Deal filter" className="inline-flex flex-wrap gap-space-2">
      {FILTER_CHIPS.map((chip) => {
        const active = chip.key === value;
        return (
          <button
            key={chip.key}
            type="button"
            role="radio"
            aria-checked={active}
            onClick={() => onChange(chip.key)}
            className={
              "inline-flex items-center px-space-3 py-[4px] rounded-pill text-caption font-display uppercase tracking-label border transition-colors " +
              (active
                ? "bg-onyx text-gold border-onyx"
                : "bg-white-pure text-gray-mid border-border-light hover:text-onyx hover:border-gold/40")
            }
          >
            {chip.label}
          </button>
        );
      })}
    </div>
  );
}
